import {
    DataDisplayView,
    FamilyGoalFundingSummary,
    FamilyGoalFundingSummaryTotal,
    FundingDisplayOptions,
} from "./models/Funding";
import React from "react";
import {
    formatFundingCurrency,
    formatFundingPercentage,
    isDollarView,
    isShowFundingForInflows,
    isShowFundingForNonInvestable,
    isShowOnlyPortfolio
} from "./fundingUtils";
import {AccordionItem, Icon} from "../../components";
import {FundingAccordionItemHeader} from "./FundingAccordionItemHeader";
import GoalFundingTable from "./GoalFundingTable";
import GoalAchievedPopover from "./GoalAchievedPopover";


type FamilyFundingSummaryProps = {
    goalsSummaryTotal: FamilyGoalFundingSummaryTotal;
    ageIndex: number;
    startAge: number;
    title: string;
    color: string;
    displayOptions: FundingDisplayOptions;
    displayView: DataDisplayView;
}

const FamilyFundingSummary: React.FC<FamilyFundingSummaryProps> = ({
                                                                       goalsSummaryTotal,
                                                                       ageIndex,
                                                                       startAge,
                                                                       title,
                                                                       color,
                                                                       displayOptions,
                                                                       displayView
                                                                   }) => {
    const dollarView = isDollarView(displayView);
    const isAchieved = !goalsSummaryTotal.presentValueAtYear?.[ageIndex];
    if(!goalsSummaryTotal.goalSummary.length) {
        return <></>
    }
    return (
        <AccordionItem uuid={title}
                       accentColor={color}
                       HeaderComponent={({expanded, toggle}) =>
                           <FundingAccordionItemHeader
                               expanded={expanded}
                               toggle={toggle}
                               title={title}
                               fundedByRiskAssets={dollarView
                                   ? formatFundingCurrency(goalsSummaryTotal.fundedByRiskAssetsAtYear?.[ageIndex])
                                   : formatFundingPercentage(goalsSummaryTotal.fundedByRiskAssetsAtYearPercentage?.[ageIndex])}
                               fundedByRiskControl={dollarView
                                   ? formatFundingCurrency(goalsSummaryTotal.fundedByRiskControlAtYear?.[ageIndex])
                                   : formatFundingPercentage(goalsSummaryTotal.fundedByRiskControlAtYearPercentage?.[ageIndex])}
                               fundedByPortfolio={formatFundingCurrency(goalsSummaryTotal.fundedByPortfolioAtYear?.[ageIndex])}
                               fundedByNonInvestable={formatFundingCurrency(goalsSummaryTotal.fundedByNonInvestableAtYear?.[ageIndex])}
                               fundedByInflows={formatFundingCurrency(goalsSummaryTotal.fundedByInflowsAtYear?.[ageIndex])}
                               presentValue={isAchieved ? <GoalAchievedPopover isHeader={true} startAge={startAge} presentValueAtYear={goalsSummaryTotal.presentValueAtYear || []}/>
                                   : formatFundingCurrency(goalsSummaryTotal.presentValueAtYear?.[ageIndex])}
                               gridClassName="funding-grid"
                               displayOptions={displayOptions}
                               isAchieved={isAchieved}
                           />
                       }
                       primaryText={title}
        >
            <FamilyFundingTable
                goalsSummary={goalsSummaryTotal.goalSummary}
                ageIndex={ageIndex}
                startAge={startAge}
                displayOptions={displayOptions}
                dollarView={dollarView}
            />
        </AccordionItem>
    );
};

type FamilyFundingTableProps = {
    goalsSummary: FamilyGoalFundingSummary[];
    ageIndex: number;
    startAge: number;
    displayOptions: FundingDisplayOptions;
    dollarView: boolean;
}

const FamilyFundingTable: React.FC<FamilyFundingTableProps> = ({
                                                                   goalsSummary,
                                                                   ageIndex,
                                                                   startAge,
                                                                   dollarView,
                                                                   displayOptions,
                                                               }) => {
    const sortedGoalSummaries = [...goalsSummary].sort((bg1, bg2) =>
            Date.parse(bg1.birthdate!).valueOf() - Date.parse(bg2.birthdate!).valueOf());
    return (
        <div className="family-summary-accordions" data-testid='family-summary-table-parent-div'>
            {sortedGoalSummaries.map((beneficiaryFundingGoal, index) => {
                if(!beneficiaryFundingGoal.goalItem.length) {
                    return <></>
                }
                const isAchieved = !beneficiaryFundingGoal.presentValueAtYear?.[ageIndex];
                return(
                    <ul key={`FamilyGoalAccordionItem${index}`}>
                        <AccordionItem
                            uuid={`FamilyGoalAccordionItem${index}`}
                            className="borderstyle-none"
                            HeaderComponent={({expanded}: { expanded: boolean }) => (
                                <div className="funding-grid funding-table-row"
                                     data-testid={`beneficiary-summary-row-${index}`} role="row">
                                    <div role="cell" className="paddingleft-md display-flex align-items-center">
                                        <Icon name={expanded ? 'chevron_down' : 'chevron_right'}
                                              size="large"/>
                                        <span className="font-md paddingleft-md">
                                            {beneficiaryFundingGoal.name}, {beneficiaryFundingGoal.age}
                                        </span>
                                        {isAchieved && <span className="paddingleft-md marginbottom-md">
                                            <Icon name="status_validated" type="success" size="small" color="#37A085" />
                                        </span>}
                                    </div>
                                    <span role="cell" className="font-md textalign-right"
                                          aria-label="Funded by Risk Assets">
                                    {dollarView
                                        ? formatFundingCurrency(beneficiaryFundingGoal.fundedByRiskAssetsAtYear?.[ageIndex])
                                        : formatFundingPercentage(beneficiaryFundingGoal.fundedByRiskAssetsAtYearPercentage?.[ageIndex])}
                                </span>
                                    <span role="cell" className="font-md textalign-right"
                                          aria-label="Funded by Risk Control">
                                    {dollarView
                                        ? formatFundingCurrency(beneficiaryFundingGoal.fundedByRiskControlAtYear?.[ageIndex])
                                        : formatFundingPercentage(beneficiaryFundingGoal.fundedByRiskControlAtYearPercentage?.[ageIndex])}
                                </span>
                                    {!isShowOnlyPortfolio(displayOptions) &&
                                        <span role="cell" className="font-md textalign-right"
                                              aria-label="Funded by Portfolio">
                                            {formatFundingCurrency(beneficiaryFundingGoal.fundedByPortfolioAtYear?.[ageIndex])}
                                        </span>}
                                    {isShowFundingForInflows(displayOptions) &&
                                        <span role="cell" className="font-md textalign-right"
                                              aria-label="Funded by Inflows">
                                            {formatFundingCurrency(beneficiaryFundingGoal.fundedByInflowsAtYear?.[ageIndex])}
                                        </span>}
                                    {isShowFundingForNonInvestable(displayOptions) &&
                                        <span role="cell" className="font-md textalign-right"
                                              aria-label="Funded by Non Investable">
                                        {formatFundingCurrency(beneficiaryFundingGoal.fundedByNonInvestableAtYear?.[ageIndex])}
                                        </span>}
                                    <span role="cell" className="font-md textalign-right"
                                          aria-label="Present Value">
                                    {isAchieved ? <GoalAchievedPopover startAge={startAge} presentValueAtYear={beneficiaryFundingGoal.presentValueAtYear || []}/>
                                        : formatFundingCurrency(beneficiaryFundingGoal.presentValueAtYear?.[ageIndex])}
                                </span>
                                </div>
                            )}
                        >
                            <GoalFundingTable
                                goals={beneficiaryFundingGoal.goalItem}
                                ageIndex={ageIndex}
                                startAge={startAge}
                                goalType="Family Goals"
                                displayOptions={displayOptions}
                                dollarView={dollarView}
                            />
                        </AccordionItem>
                    </ul>
                )
            })}
        </div>
    )
};

export default FamilyFundingSummary;